import { block, requestErrorMessageFactory } from '@softline/application';
import { MessageBarRef, messageFn, translator } from '@softline/ui-core';
import { inject, Injector, Signal } from '@angular/core';
import { Message } from '@softline/core';

export type RequestOptions = LoadingRequestOptions | SavingRequestOptions | BlockingRequestOptions

export interface RequestOptionsBase {
  successMessage?: Signal<string>;
  customErrorHandler?: (error: unknown) => Message | null
}

export interface LoadingRequestOptions extends RequestOptionsBase {
  type: 'loading';
  loadingMessage: Signal<string>;
}

export interface SavingRequestOptions extends RequestOptionsBase {
  type: 'saving';
  savingMessage: Signal<string>;
}

export interface BlockingRequestOptions extends RequestOptionsBase {
  type: 'blocking';
}

export function requestFn() {
  const message = messageFn();
  const injector = inject(Injector);
  const t = translator();
  return async <T>(params: { request: () => Promise<T>, options?: RequestOptions }): Promise<T | null> => {
    let result: T | null = null;
    let messageBarRef: MessageBarRef | null = null;
    try {
      switch (params.options?.type) {
        case 'loading':
          messageBarRef = message.loading(params.options.loadingMessage);
          result = await params.request();
        break;
        case 'saving':
          messageBarRef = message.saving(params.options.savingMessage);
          result = await params.request();
        break;
        case 'blocking':
          result = await block(async () => await params.request(), injector);
        break;
        default:
          result = await params.request();
        break;
      }

      if(messageBarRef)
        messageBarRef.dismiss();

      if (params.options?.successMessage)
        message.success(params.options.successMessage);
    }

    catch (error) {
      let errorMessage: Message | null = null;
      if(messageBarRef)
        messageBarRef.dismiss()
      if(params.options?.customErrorHandler)
        errorMessage = params.options.customErrorHandler(error);
      if(!errorMessage)
        errorMessage = requestErrorMessageFactory(error);

      if(!errorMessage) {
        throw error;
      }

      const subject = errorMessage.subject ? t(errorMessage.subject, errorMessage.data as any): undefined;
      const text = t(errorMessage.message, errorMessage.data as any);

      logRequestError(errorMessage.type, subject ? subject() : null, text(), error);
      switch (errorMessage.type) {
        case 'critical':
        case 'error':
          message.error(text, subject);
          break;
        case 'warning':
          message.warning(text, subject);
          break;
        case 'info':
          message.info(text, subject);
          break;
        case 'success':
          message.success(text, subject);
          break;
        default:
          message.error(text, subject);
          break;
      }
    }
    return result;
  }
}

function logRequestError(type: string, subject: string | null, text: string, error: unknown): void {
  switch (type) {
    case 'critical':
      console.error('Softapps Critical error:', subject ?? 'Critical error', '\n', text, '\n', error);
      break;
    case 'error':
      console.error('Softapps Error:', subject ?? 'Error', '\n', text, '\n\n', error);
      break;
    case 'warning':
      console.warn('Softapps Warning:', subject ?? 'Warning', '\n', text, '\n\n', error);
      break;
    case 'info':
      console.info('Softapps Info:', subject ?? 'Info', '\n', text, '\n\n', error);
      break;
    case 'success':
      console.debug('Softapps Success:', subject ?? 'Success', '\n', text, '\n\n', error);
      break;
    default:
      console.error('Softapps', subject ?? 'Unknown Error', error, '\n', text, '\n\n', error);
  }
}
